import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { ProjectIllustration } from './ProjectIllustration';
import type { Lang, Project } from '../types/profile';
interface ProjectRowProps {
  project: Project;
  index: number;
  lang: Lang;
}
export function ProjectRow({
  project,
  index,
  lang
}: ProjectRowProps) {
  const isThai = lang === 'th';
  const number = String(index + 1).padStart(2, '0');
  return <motion.li data-reveal initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.7,
    ease: [0.22, 1, 0.36, 1],
    delay: 0.04 * index
  }} className="project-row print-avoid group relative grid gap-5 border-t border-line py-10 md:grid-cols-12 md:gap-8 md:py-12" data-id="element-103" data-mp="2bxcsf8xkfiju">
      <span aria-hidden="true" className="decor absolute -top-px left-0 h-px w-0 bg-sage-deep transition-[width] duration-700 ease-out group-hover:w-full" data-id="element-104" data-mp="68r2cnh8ll2" />

      <div className="flex items-start justify-between md:col-span-1 md:block" data-id="element-105" data-mp="3me9ndbma4ls2">
        <span className="font-mono text-[13px] tracking-wider text-ink-soft transition-colors duration-300 group-hover:text-sage-deep" data-id="element-106" data-mp="3hgq5m1qdhqvd">
          {number}
        </span>
        <ProjectIllustration id={project.id} className="h-16 w-28 md:hidden" data-id="element-107" data-mp="3r600fpclpnn6" />
      </div>

      <div className="md:col-span-4" data-id="element-108" data-mp="3r8dubuvjott5">
        <h3 className={`font-display text-charcoal transition-colors duration-300 group-hover:text-sage-deep ${isThai ? 'text-[1.6rem] leading-[1.4] md:text-[1.8rem]' : 'text-[1.75rem] leading-[1.1] tracking-[-0.01em] md:text-[2rem]'}`} data-id="element-109" data-mp="33fnkbr77ckli">
          {project.title}
        </h3>

        {project.status && <span className="mt-3 inline-flex items-center gap-2 rounded-full border border-sage/50 bg-sage-tint px-3 py-1 text-[13px] font-medium text-sage-deep" data-id="element-110" data-mp="rqmkab3pv36r">
            <span className="relative flex h-1.5 w-1.5" data-id="element-111" data-mp="mrjshscqytgq">
              <span className="decor absolute inline-flex h-full w-full animate-ping rounded-full bg-sage-deep/50 motion-reduce:animate-none" data-id="element-112" data-mp="3gkkq068dxgbt" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-sage-deep" data-id="element-113" data-mp="3lkz0duxgl2d0" />
            </span>
            {project.status}
          </span>}

        <ul className="mt-4 flex flex-wrap gap-2" data-id="element-114" data-mp="3uihml2mu9mqh">
          {project.tags.map(tag => <li key={tag} className="rounded-full border border-line px-2.5 py-0.5 text-[13px] leading-6 text-ink-soft" data-id="element-115" data-mp="2xubf87yj8g8i">
              {tag}
            </li>)}
        </ul>
      </div>

      <div className="md:col-span-5 print:col-span-7" data-id="element-116" data-mp="36snawoe9a47t">
        <p className={`text-[17px] text-charcoal/90 ${isThai ? 'leading-[1.95]' : 'leading-[1.75]'}`} data-id="element-117" data-mp="3bkycdvon9y2h">
          {project.description}
        </p>
        {project.url && <a href={project.url} target="_blank" rel="noopener noreferrer" className="no-print mt-5 inline-flex items-center gap-2 rounded-sm border-b border-sage-deep pb-1 text-[15px] font-medium text-sage-deep transition-colors hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage/60" data-id="element-118" data-mp="2j4464iuf09kh">
            {isThai ? 'ดูเว็บไซต์โปรเจกต์' : 'View project website'}
            <ExternalLink className="h-4 w-4" aria-hidden="true" data-id="element-119" data-mp="1xkk1zbhhac4z" />
          </a>}
      </div>

      <div className="hidden justify-end md:col-span-2 md:flex print:hidden" data-id="element-120" data-mp="1snle3aoer1f8">
        <ProjectIllustration id={project.id} className="h-20 w-32 transition-transform duration-500 ease-out group-hover:-translate-y-1 motion-reduce:transition-none" data-id="element-121" data-mp="1nu8x9mpyzyx1" />
      </div>
    </motion.li>;
}